import React from 'react';

import * as commonTypes from '../../../../../../typings/api/skymap/rest/v1/.common';
import { FolderModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { isDefined } from '../../../../utils/variables';
import { useNonce } from '../../../hooks/use-nonce';
import { AppContext, CurrentUser } from '../../../state/app-state';
import { ProjectContext } from '../../../state/project-state/project-state';

export interface TreeContext {
  selectedFolderId?: string;
  /**
   * Selects a folder. Undefined means root folder is selected (default).
   */
  selectFolder: (folderId?: string) => void;
  selectAndOpenFolder: (folderId?: string) => void;
  isFolderOpened: (folderId: string) => boolean;
  openFolder: (folderId: string) => void;
  closeFolder: (folderId: string) => void;
  refreshNonce: number;
  setRefreshNonce: () => void;
  folders: commonTypes.FolderModel[];
  setFolders: React.Dispatch<React.SetStateAction<commonTypes.FolderModel[]>>;
  selectedFolderChain: commonTypes.FolderModel[];
  hasWriteAccessToCurrentFolder: (currentUser?: CurrentUser) => boolean;
  hasWriteAccessToFolder: (currentUser?: CurrentUser, folder?: FolderModel) => boolean;
  isCurrentFolderWithinActiveConnectFolder: boolean;
}

export const TreeContext = React.createContext<TreeContext>({
  selectedFolderId: undefined,
  selectFolder: (folderId?: string) => {
    /* No default implementation */
  },
  selectAndOpenFolder: (folderId?: string) => {
    /* No default implementation */
  },
  isFolderOpened: (folderId: string) => false,
  openFolder: (folderId: string) => {
    /* No default implementation */
  },
  closeFolder: (folderId: string) => {
    /* No default implementation */
  },
  refreshNonce: 0,
  setRefreshNonce: () => {
    /* No default implementation */
  },
  folders: [],
  setFolders: () => {
    /* No default implementation */
  },
  selectedFolderChain: [],
  hasWriteAccessToCurrentFolder: (currentUser?: CurrentUser) => false,
  hasWriteAccessToFolder: (currentUser?: CurrentUser, folder?: FolderModel) => false,
  isCurrentFolderWithinActiveConnectFolder: false,
});

type OpenedFoldersState = string[];
type OpenedFoldersAction = { type: 'add'; folderId: string } | { type: 'remove'; folderId: string };

type Props = {
  children: React.ReactNode;
};

const FolderTreeState = (props: Props) => {
  const [refreshNonce, setRefreshNonce] = useNonce();

  const { isAdminForCompany } = React.useContext(AppContext);
  const { project } = React.useContext(ProjectContext);
  const [folders, setFolders] = React.useState<commonTypes.FolderModel[]>([]);
  const [selectedFolderChain, setSelectedFolderChain] = React.useState<commonTypes.FolderModel[]>(
    [],
  );
  const [selectedFolderId, setSelectedFolderId] = React.useState<string>();
  const [isCurrentFolderWithinActiveConnectFolder, setIsCurrentFolderWithinActiveConnectFolder] =
    React.useState<boolean>(false);
  const [openedFolders, dispatchOpenedFolders] = React.useReducer(openedFoldersReducer, []);

  const resolveChain = React.useCallback(
    (folderId: string | null): commonTypes.FolderModel[] => {
      const folder = folders.find((x) => x.id === folderId)!;
      return folder ? [...resolveChain(folder.parentId), folder] : [];
    },
    [folders],
  );

  React.useEffect(() => {
    setSelectedFolderChain(selectedFolderId ? resolveChain(selectedFolderId) : []);
  }, [selectedFolderId, resolveChain]);

  React.useEffect(() => {
    setIsCurrentFolderWithinActiveConnectFolder(
      selectedFolderChain.length > 0
        ? isDefined(selectedFolderChain[0].skyMapConnectFolder)
        : false,
    );
  }, [setIsCurrentFolderWithinActiveConnectFolder, selectedFolderChain]);

  function getParentFolders(folderId: string) {
    const parentFolders: commonTypes.FolderModel[] = [];

    let folder = folders.find((x) => x.id === folderId);
    while (folder?.parentId) {
      folder = folders.find((x) => x.id === folder!.parentId)!;
      parentFolders.push(folder);
    }

    return parentFolders;
  }

  function openedFoldersReducer(state: OpenedFoldersState, action: OpenedFoldersAction) {
    switch (action.type) {
      case 'add': {
        // Get list of folder ids to expand (current folder and all parent folders).
        const folderIdsToExpand = [
          ...getParentFolders(action.folderId).map((x) => x.id),
          action.folderId,
        ];

        // Only expand current folder if it has sub folders.
        if (folders.some((x) => x.parentId === action.folderId)) {
          folderIdsToExpand.push(action.folderId);
        }

        // Return unique list of ids after merging folder ids to open with current state.
        return [...new Set([...state, ...folderIdsToExpand])];
      }

      case 'remove':
        return state.filter((x) => x !== action.folderId);
    }
  }

  const isFolderOpened = (folderId: string) => openedFolders.includes(folderId);

  const openFolder = React.useCallback((folderId: string) => {
    dispatchOpenedFolders({ type: 'add', folderId });
  }, []);

  const closeFolder = React.useCallback(
    (folderId: string) => dispatchOpenedFolders({ type: 'remove', folderId }),
    [],
  );

  const selectFolder = React.useCallback((folderId?: string) => {
    setSelectedFolderId(folderId);
  }, []);

  const selectAndOpenFolder = React.useCallback(
    (folderId?: string) => {
      selectFolder(folderId);

      // undefined == root ("project folder") and cannot be collapsed.
      if (folderId) {
        openFolder(folderId);
      }
    },
    [openFolder, selectFolder],
  );

  const hasWriteAccessToFolder = (currentUser?: CurrentUser, folder?: FolderModel): boolean => {
    // Guests never have write access
    if (!isDefined(currentUser)) {
      return false;
    }

    // Administrators always have write access.
    if (isAdminForCompany(project?.companyId)) {
      return true;
    }

    // No access for other users when root folder (project) is selected.
    if (!folder) {
      return false;
    }

    // Find first level folder (where tags are applied on).
    let currentFolderLevel = folders.find((x) => x.id === folder.id)!;
    while (currentFolderLevel.parentId) {
      currentFolderLevel = folders.find((x) => x.id === currentFolderLevel.parentId)!;
    }

    // Check whether current user has at least one tag with write permission that matches
    // folder tags.
    const result = (currentUser.projectUserTags ?? [])
      .filter((tag) => tag.projectId === folder.projectId && tag.permission === 'WRITE')
      .some((tag) => currentFolderLevel.tags?.find((x) => x.id === tag.tagId));

    return result;
  };

  // Checking write access should be refactored to Cerbos.
  // Adding in another ticket.
  // TODO: https://skymapinnovations.atlassian.net/browse/SK-4694
  const hasWriteAccessToCurrentFolder = (currentUser?: CurrentUser): boolean => {
    // Find first level folder (where tags are applied on).
    const folder = folders.find((x) => x.id === selectedFolderId)!;
    return hasWriteAccessToFolder(currentUser, folder);
  };

  return (
    <TreeContext.Provider
      value={{
        selectedFolderId,
        selectFolder,
        selectAndOpenFolder,
        isFolderOpened,
        openFolder,
        closeFolder,
        refreshNonce,
        setRefreshNonce,
        folders,
        setFolders,
        selectedFolderChain,
        hasWriteAccessToCurrentFolder,
        hasWriteAccessToFolder,
        isCurrentFolderWithinActiveConnectFolder,
      }}
    >
      {props.children}
    </TreeContext.Provider>
  );
};

export { FolderTreeState };
